import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useVisitor } from '../context/VisitorContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PHP_CODE, README_TXT, APP_SCRIPT_JS } from '../utils/wpPluginCode';

const { FiCode, FiCopy, FiCheck, FiDownload, FiInfo, FiServer, FiGlobe, FiRefreshCw } = FiIcons;

const EmbedScript = () => {
  const { projects, credentials, blacklist } = useVisitor();
  const [activeTab, setActiveTab] = useState('js'); // 'js' or 'wp'
  const [selectedProject, setSelectedProject] = useState('');
  const [copied, setCopied] = useState(false);
  const [isZipping, setIsZipping] = useState(false);

  const APP_URL = "https://shiny-puffpuff-add900.netlify.app";

  // --- JS Snippet Logic ---
  const generateEmbedScript = (projectId) => {
    const configObj = {
      projectId: projectId,
      apiEndpoint: `${APP_URL}/api/track`,
      supabaseUrl: credentials.url || 'YOUR_SUPABASE_URL',
      supabaseKey: credentials.key || 'YOUR_SUPABASE_ANON_KEY'
    };

    return `<!-- Visitor Tracker -->
<script>
(function() {
  window.VisitorTracker = ${JSON.stringify(configObj, null, 2)};
  var s = document.createElement('script');
  s.src = '${APP_URL}/tracker.js';
  s.async = true;
  document.head.appendChild(s);
})();
</script>`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // --- WP Plugin Logic ---
  const downloadPlugin = async () => {
    setIsZipping(true);
    try {
      const zip = new JSZip();

      // 1. DYNAMICALLY INJECT BLACKLIST
      let finalPhpCode = PHP_CODE;
      
      // If user has a blacklist in React Context, verify it's valid and inject it
      if (blacklist && blacklist.length > 0) {
        // Convert JS objects to PHP array syntax
        // ['Pattern', 'Category'],
        const phpArrayItems = blacklist.map(item => {
          // Escape single quotes for PHP safety
          const safePattern = item.pattern.replace(/'/g, "\\'");
          const safeCategory = item.category ? item.category.replace(/'/g, "\\'") : 'ISP';
          return `['${safePattern}', '${safeCategory}']`;
        }).join(",\n        ");

        // Regex replace the placeholder block in PHP_CODE 
        // Looks for /* DYNAMIC_BLACKLIST_START */ ... /* DYNAMIC_BLACKLIST_END */
        // We use a string for the regex to avoid escaping issues in this source file
        const regex = /\/\* DYNAMIC_BLACKLIST_START \*\/[\s\S]*?\/\* DYNAMIC_BLACKLIST_END \*\//;
        
        // Replacement string (keeping the comments for safety/debugging)
        const replacement = `/* DYNAMIC_BLACKLIST_START */
        ${phpArrayItems}
        /* DYNAMIC_BLACKLIST_END */`;
        
        finalPhpCode = finalPhpCode.replace(regex, replacement);
      }

      const folder = zip.folder("simple-visitor-tracker");
      folder.file("simple-visitor-tracker.php", finalPhpCode);
      folder.file("readme.txt", README_TXT);
      folder.file("google-apps-script.js", APP_SCRIPT_JS);

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "simple-visitor-tracker.zip");
    } catch (err) {
      console.error("Failed to zip:", err);
      alert("Error creating zip file.");
    } finally {
      setIsZipping(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-8"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Integration</h1>
        <p className="text-gray-600">Choose how you want to track visitors</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-8 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('js')}
          className={`pb-4 px-2 font-medium text-sm flex items-center transition-colors ${
            activeTab === 'js'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <SafeIcon icon={FiGlobe} className="mr-2" />
          JavaScript Snippet (Cloud)
        </button>
        <button
          onClick={() => setActiveTab('wp')}
          className={`pb-4 px-2 font-medium text-sm flex items-center transition-colors ${
            activeTab === 'wp'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <SafeIcon icon={FiServer} className="mr-2" />
          WordPress Plugin (Self-Hosted)
        </button>
      </div>

      {/* --- Tab Content: JS Snippet --- */}
      {activeTab === 'js' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Project</h2>
            {projects.length > 0 ? (
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a project...</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.url})
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiInfo} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">No projects found. Please add a project first.</p>
              </div>
            )}
          </div>

          {selectedProject && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Copy Embed Code</h2>
                <button
                  onClick={() => copyToClipboard(generateEmbedScript(selectedProject))}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    copied ? 'bg-green-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} />
                  <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                </button>
              </div>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm">
                  <code>{generateEmbedScript(selectedProject)}</code>
                </pre>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <SafeIcon icon={FiCode} className="mr-2" />
                    How to install
                  </h3>
                  <p className="text-sm text-blue-800">
                    Paste this code inside the <code>&lt;head&gt;</code> tag of every page you want to track.
                  </p>
                </div>
                <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                    <SafeIcon icon={FiCheck} className="mr-2" />
                    What it tracks
                  </h3>
                  <p className="text-sm text-purple-800">
                    Company names (via IP), landing pages, UTM tags, session duration, and user journeys.
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* --- Tab Content: WordPress Plugin --- */}
      {activeTab === 'wp' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                <SafeIcon icon={FiServer} className="text-4xl text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Simple Visitor Tracker (WP Plugin)</h2>
                <p className="text-gray-600 mt-2">
                  A standalone WordPress plugin that stores data in your local WordPress database. Ideal if you don't want to use the Cloud Dashboard.
                </p>
                <div className="flex gap-2 mt-3">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Version 4.3.2</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">Self-Hosted</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-2">1. Configure (Optional)</h3>
                <p className="text-sm text-gray-600">Update your Blacklist in the "Blacklist" tab first. It will be auto-included.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-2">2. Download</h3>
                <p className="text-sm text-gray-600">Get the ZIP file. It generates a unique version with your settings.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-2">3. Install</h3>
                <p className="text-sm text-gray-600">Upload to your WordPress Plugins page and activate.</p>
              </div>
            </div>

            <button
              onClick={downloadPlugin}
              disabled={isZipping}
              className="w-full md:w-auto px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              {isZipping ? (
                <>
                  <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                  Generating Custom Plugin...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDownload} className="mr-2" />
                  Download Plugin (v4.3.2.zip)
                </>
              )}
            </button>
            <p className="text-center text-sm text-gray-400 mt-4">
              Includes: simple-visitor-tracker.php (with your {blacklist.length} blacklist rules), readme.txt, google-apps-script.js
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EmbedScript;